// amazonPay.js
import Vue from 'vue';
import gql from 'graphql-tag';

export default {
    namespaced: true,
    state: {
        config: {},
        checkoutSessionId: null,
        paymentProcessing: false,
        paymentCompleted: false,
    },
    mutations: {
        setConfig(state, value) {
            state.config = value;
        },
        setPaymentProcessing(state, value) {
            state.paymentProcessing = value;
        },
        setPaymentCompleted(state, value) {
            state.paymentCompleted = value;
        },
    },
    actions: {
        loadPaymentConfiguration({ commit }) {
            return Vue.$graphql
                .query({
                    query: gql`
                        query getAmazonPayFrontendConfiguration {
                            getAmazonPayFrontendConfiguration {
                                clientId
                                merchantId
                                sandbox
                                buttonColor
                                currencyCode
                            }
                        }
                    `,
                })
                .then(({ data }) => {
                    if (data.getAmazonPayFrontendConfiguration) {
                        commit('setConfig', data.getAmazonPayFrontendConfiguration);
                        return data.getAmazonPayFrontendConfiguration;
                    }
                })
                .catch(error => {
                    throw new Error(`Failed to load payment configuration: ${error.message}`);
                });
        },
        createCheckoutSession({ commit }, options) {
            return Vue.$graphql
                .mutate({
                    mutation: gql`
                        mutation createCheckoutSession(
                            $orderId: Int!
                            $transactionId: String!
                            $shippingAddress: JSON!
                        ) {
                            createCheckoutSession(
                                orderId: $orderId
                                transactionId: $transactionId
                                shippingAddress: $shippingAddress
                            ) {
                                payload
                                signature
                                publicKeyId
                            }
                        }
                    `,
                    variables: {
                        orderId: parseInt(options.orderId),
                        transactionId: options.transactionId,
                        shippingAddress: options.shippingAddress,
                    },
                })
                .then(({ data }) => {
                    if (data.createCheckoutSession) {
                        return data.createCheckoutSession;
                    }
                })
                .catch(error => {
                    throw new Error(`Failed to create checkout session: ${error.message}`);
                });
        },
        setPaymentInfo({ commit }, options) {
            return Vue.$graphql
                .mutate({
                    mutation: gql`
                        mutation setAmazonPaymentInfo(
                            $orderId: Int!
                            $transactionId: String!
                            $checkoutSessionId: String!
                        ) {
                            setAmazonPaymentInfo(
                                orderId: $orderId
                                transactionId: $transactionId
                                checkoutSessionId: $checkoutSessionId
                            ) {
                                amazonPayRedirectUrl
                                error
                            }
                        }
                    `,
                    variables: {
                        orderId: parseInt(options.orderId),
                        transactionId: options.transactionId,
                        checkoutSessionId: options.amazonCheckoutSessionId
                    },
                })
                .then(({ data }) => {
                    if (data.setAmazonPaymentInfo) {
                        return data.setAmazonPaymentInfo;
                    }
                })
                .catch(error => {
                    throw new Error(`Failed to set amazon payment info: ${error.message}`);
                });
        },
        getAmazonAddressInfo({ commit }, options) {
            return Vue.$graphql
                .mutate({
                    mutation: gql`
                        mutation getAmazonAddressInfo(
                            $checkoutSessionId: String!
                        ) {
                            getAmazonAddressInfo(
                                checkoutSessionId: $checkoutSessionId
                            ) {
                                checkoutSessionId
                                amazonPayRedirectUrl
                                checkoutReviewReturnUrl
                                releaseEnvironment
                                error
                                buyer {
                                    name
                                    email
                                    buyerId
                                    phoneNumber
                                }
                                billingAddress {
                                    name
                                    addressLine1
                                    addressLine2
                                    addressLine3
                                    city
                                    county
                                    district
                                    stateOrRegion
                                    postalCode
                                    countryCode
                                    phoneNumber
                                }
                                shippingAddress {
                                    name
                                    addressLine1
                                    addressLine2
                                    addressLine3
                                    city
                                    county
                                    district
                                    stateOrRegion
                                    postalCode
                                    countryCode
                                    phoneNumber
                                }
                            }
                        }
                    `,
                    variables: {
                        checkoutSessionId: options.amazonCheckoutSessionId
                    },
                })
                .then(({ data }) => {
                    if (data.getAmazonAddressInfo) {
                        return data.getAmazonAddressInfo;
                    }
                })
                .catch(error => {
                    throw new Error(`Failed to get Amazon address info: ${error.message}`);
                });
        },
        createBasketCheckoutSession({ commit }) {
            return Vue.$graphql
                .mutate({
                    mutation: gql`
                        mutation createBasketCheckoutSession {
                            createBasketCheckoutSession {
                                payload
                                signature
                                publicKeyId
                            }
                        }
                    `
                })
                .then(({ data }) => {
                    if (data.createBasketCheckoutSession) {
                        return data.createBasketCheckoutSession;
                    }
                })
                .catch(error => {
                    throw new Error(`Failed to create checkout session: ${error.message}`);
                });
        },
        executePayment({ commit }, options) {
            commit('Payment/setError', null, { root: true });
            commit('setPaymentCompleted', false);
            return Vue.$graphql
                .mutate({
                    mutation: gql`
                        mutation amazonExecutePayment(
                            $orderId: String!
                            $transactionId: String!
                            $checkoutSessionId: String!
                        ) {
                            amazonExecutePayment(
                                orderId: $orderId
                                transactionId: $transactionId
                                checkoutSessionId: $checkoutSessionId
                            ) {
                                success
                                error
                            }
                        }
                    `,
                    variables: {
                        orderId: options.orderId,
                        transactionId: options.transactionId,
                        checkoutSessionId: options.amazonCheckoutSessionId
                    }
                })
                .then(r => r.data)
                .then(data => {
                    commit('setPaymentCompleted', data.amazonExecutePayment.success);
                    return data.amazonExecutePayment;
                })
                .catch(error => {
                    if (error.message) {
                        commit('Payment/setError', error.message, { root: true });
                    }
                    return {
                        result: null,
                        error: error.message,
                    };
                });
        }
    },
};
